<template>
  <div>
    <!-- Top Row: 6 KPI Summary Cards -->
    <v-row v-if="havePermissionGetSubscriberStatistics" class="mb-4">
      <v-col
        cols="6"
        sm="4"
        md="2"
        v-for="(statistics, index) in statisticsData"
        :key="index"
        class="pb-2"
      >
        <statistics-card :data="statistics" @onClick="onCardClick(index)" />
      </v-col>
    </v-row>

    <!-- Second Row: Statistics Chart & Notifications Card -->
    <v-row>
      <v-col v-if="havePermissionGetSubscriberStatistics" cols="12" md="8">
        <v-row align="center" class="mb-2">
          <v-col cols="12" sm="5" class="text-start">
            <div class="textLight--text font-weight-bold text-h5">
              {{ $t("statistics.statistics") }}
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              color="primary"
              hide-details
              v-model="selectedCard"
              :items="graphTypeArr"
              item-value="value"
              item-text="text"
              flat
              solo
              dense
              class="rounded-lg"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="3">
            <v-select
              color="primary"
              hide-details
              v-model="chartType"
              :items="chartTypeArr"
              item-value="value"
              item-text="text"
              flat
              solo
              dense
              class="rounded-lg"
            ></v-select>
          </v-col>
        </v-row>

        <div class="mt-3">
          <line-chart :type="selectedCard" :data="chartData" />
        </div>
      </v-col>

      <v-col
        v-if="havePermissionGetNotifications"
        cols="12"
        :md="havePermissionGetSubscriberStatistics ? '4' : '12'"
      >
        <notification-list />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import LineChart from "./LineChart.vue";
import StatisticsCard from "./StatisticsCard.vue";
import NotificationList from "../../core-module/components/NotificationAlertList.vue";

import { hasPermission } from "@/app/user-module/helpers/authHelper";

export default {
  components: {
    LineChart,
    StatisticsCard,
    NotificationList,
  },
  data() {
    return {
      chartType: "monthly",
      chartTypeArr: [
        { text: "Monthly", value: "monthly" },
        { text: "This Month", value: "thisMonth" },
        { text: "This Week", value: "thisWeek" },
        { text: "Today", value: "today" },
      ],
      graphTypeArr: [
        {
          text: this.$t("statistics.totalRegistrations"),
          value: "REGISTRATION",
        },
        { text: this.$t("statistics.totalTrainings"), value: "TRAINING" },
        { text: "Total Trainers", value: "TRAINER" },
        { text: this.$t("statistics.totalEmployees"), value: "EMPLOYEE" },
        {
          text: this.$t("statistics.totalOrganizations"),
          value: "ORGANIZATION",
        },
        { text: this.$t("statistics.totalRevenue"), value: "REVENUE" },
      ],
      chartData: {
        labels: [],
        datasets: [{ data: [] }],
      },
      graphStatisticsData: {},
      selectedCard: "REGISTRATION",
      statisticsData: [
        {
          value: 0,
          name: "totalRegistrations",
          text: this.$t("statistics.totalRegistrations"),
          icon: "mdi-account-supervisor-outline",
          divider: true,
          selected: false,
          graphType: "REGISTRATION",
          routeName: "Registrations",
        },
        {
          value: 0,
          name: "totalTrainings",
          text: this.$t("statistics.totalTrainings"),
          icon: "mdi-book-open-page-variant-outline",
          divider: true,
          selected: false,
          graphType: "TRAINING",
          routeName: "Courses",
        },
        {
          value: 0,
          name: "totalTrainers",
          text: "Total Trainers",
          icon: "mdi-account-group-outline",
          divider: true,
          selected: false,
          graphType: "TRAINER",
          routeName: "EmployeesRoles",
        },
        {
          value: 0,
          name: "totalEmployees",
          text: this.$t("statistics.totalEmployees"),
          icon: "mdi-office-building-outline",
          divider: true,
          selected: false,
          graphType: "EMPLOYEE",
          routeName: "EmployeesRoles",
        },
        {
          value: 0,
          name: "totalOrganizations",
          text: this.$t("statistics.totalOrganizations"),
          icon: "mdi-domain",
          divider: true,
          selected: false,
          graphType: "ORGANIZATION",
          routeName: "Organizations",
        },
        {
          value: 0,
          name: "totalRevenue",
          text: this.$t("statistics.totalRevenue"),
          icon: "mdi-currency-usd",
          divider: false,
          selected: false,
          graphType: "REVENUE",
          routeName: "Reports",
        },
      ],
    };
  },
  computed: {
    havePermissionGetSubscriberStatistics() {
      return hasPermission({
        currentRole: this.$store.state.authenticatedUserRole,
        currentPermissions: this.$store.state.authenticatedUserPermissions,
        requiredPermission: ["GET_SUBSCRIBER_STATISTICS"],
        restrictOrganizationManager:
          this.$store.state.authenticatedUser?.isOrganizationManager,
      });
    },
    havePermissionGetNotifications() {
      return !this.$store.state.authenticatedUser?.isOrganizationManager;
    },
  },
  apollo: {
    getSubscriberStatistics() {
      return {
        query: require("../api/query/getSubscriberStatistics.gql"),
        result({ data }) {
          if (data && data.getSubscriberStatistics) {
            Object.entries(data.getSubscriberStatistics).forEach(
              ([key, value]) => {
                this.statisticsData = this.statisticsData.map(data => {
                  if (data.name === key) {
                    if (key === "totalRevenue") {
                      data.value = value ? "$ " + value : "$ " + 0;
                    } else {
                      data.value = value ? value : 0;
                    }
                  }
                  return data;
                });
              }
            );
          }
        },
        skip() {
          return !this.havePermissionGetSubscriberStatistics;
        },
      };
    },
    getGraphStatistics() {
      return {
        query: require("@/app/statistics-module/api/query/getGraphStatistics.gql"),
        variables: {
          type: "REGISTRATION",
        },
        result({ data }) {
          if (data && data.getGraphStatistics) {
            this.chartData.labels = [];
            this.chartData.datasets[0].data = [];

            this.graphStatisticsData = data.getGraphStatistics;
            if (
              this.graphStatisticsData[this.chartType] &&
              this.graphStatisticsData[this.chartType].length
            ) {
              this.graphStatisticsData[this.chartType].map((obj, index) => {
                this.chartData.labels[index] = obj._id;
                this.chartData.datasets[0].data[index] = obj.value;
              });
            }
          }
        },
        skip() {
          return !this.havePermissionGetSubscriberStatistics;
        },
      };
    },
  },
  created() {
    this.$apollo.queries.getGraphStatistics.refetch();
    this.$apollo.queries.getSubscriberStatistics.refetch();
  },
  watch: {
    chartType: {
      immediate: false,
      handler() {
        if (this.graphStatisticsData) {
          this.chartData.labels = [];
          this.chartData.datasets[0].data = [];
          if (
            this.graphStatisticsData[this.chartType] &&
            this.graphStatisticsData[this.chartType].length
          ) {
            this.graphStatisticsData[this.chartType].map((obj, index) => {
              this.chartData.labels[index] = obj._id;
              this.chartData.datasets[0].data[index] = obj.value;
            });
          }
        }
      },
    },
    selectedCard() {
      if (this.selectedCard) {
        this.$apollo.queries.getGraphStatistics.refetch({
          type: this.selectedCard,
        });
      }
    },
  },
  methods: {
    async onCardClick(i) {
      if (this.statisticsData[i]?.routeName) {
        if (this.statisticsData[i]?.name === "totalRevenue") {
          this.$router.push({
            name: this.statisticsData[i]?.routeName,
            params: { data: 1 },
          });
        } else if (this.statisticsData[i]?.name === "totalTrainers") {
          this.$router.push({
            name: this.statisticsData[i]?.routeName,
            params: { role: "TRAINER" },
          });
        } else {
          this.$router.push({
            name: this.statisticsData[i]?.routeName,
          });
        }
      }
    },
  },
};
</script>
